import { useMemo, useState } from "react";
import { AppAction, ReportType } from "@edlab-fms/shared";
import { useSearchParams } from "react-router-dom";
import { useAuthQuery } from "../lib/api/auth";
import { useProgrammesOverviewQuery } from "../lib/api/overview";
import {
  useBankAccountsQuery,
  useCategoriesQuery,
  useFundsQuery,
} from "../lib/api/reference";
import {
  useTransactionsQuery,
  type TransactionListItem,
  type TransactionTab,
} from "../lib/api/transactions";
import { formatMoney } from "../lib/format/money";
import { expenseStatusTone } from "../lib/format/status";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Chip, ChipGroup } from "../components/ui/Chip";
import { SearchInput } from "../components/ui/SearchInput";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/ui/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/Table";
import { NewExpenseModal } from "../features/transactions/NewExpenseModal";
import { RecordIncomeModal } from "../features/transactions/RecordIncomeModal";
import { TransactionDetailModal } from "../features/transactions/TransactionDetailModal";
import { flattenProgrammeUnits } from "../features/transactions/transaction-utils";
import { buildReportFilters, useExportReportMutation } from "../lib/api/reports";
import { MonthSelector } from "../components/ui/MonthSelector";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";

const TABS: { id: TransactionTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "income", label: "Income" },
  { id: "expense", label: "Expenses" },
  { id: "submitted", label: "To approve" },
  { id: "approved", label: "Approved" },
  { id: "part_paid", label: "Part-paid" },
  { id: "paid", label: "Paid" },
  { id: "draft", label: "Draft" },
  { id: "evidence", label: "Evidence" },
];

function parseTab(value: string | null): TransactionTab {
  return TABS.some((entry) => entry.id === value) ? (value as TransactionTab) : "all";
}

export function TransactionsPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));
  const [search, setSearch] = useState("");
  const { period, setPeriod: setExportMonth, months: exportMonths } = useReportingPeriod();
  const [selected, setSelected] = useState<TransactionListItem | null>(null);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const transactions = useTransactionsQuery(tab, search);
  const programmes = useProgrammesOverviewQuery(Boolean(user), period);
  const funds = useFundsQuery(Boolean(user));
  const incomeCategories = useCategoriesQuery("income", Boolean(user));
  const expenseCategories = useCategoriesQuery("expense", Boolean(user));
  const bankAccounts = useBankAccountsQuery(Boolean(user));

  const units = useMemo(
    () => flattenProgrammeUnits(programmes.data?.groups),
    [programmes.data?.groups],
  );

  const canCreateReceipt = user?.permissions.actions.includes(AppAction.RECEIPT_CREATE);
  const canCreateExpense = user?.permissions.actions.includes(AppAction.EXPENSE_CREATE);
  const canExport = user?.permissions.actions.includes(AppAction.REPORT_EXPORT);
  const exportReport = useExportReportMutation();

  function transactionExportFilters() {
    return buildReportFilters({
      reportType: ReportType.TRANSACTIONS,
      month: period,
      transactionTab: tab,
      search,
    });
  }

  const pendingCount =
    transactions.data?.transactions.filter(
      (item) => item.kind === "expense" && item.status === "submitted",
    ).length ?? 0;

  function selectTab(nextTab: TransactionTab): void {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === "all") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", nextTab);
    }
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <div>
      <PageHeader
        eyebrow="Transactions"
        title="Every transaction, in one place."
        description="Record once. Follow each expense from submission to payment."
        actions={
          <>
            {canCreateReceipt ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setIncomeOpen(true);
                }}
              >
                Record income
              </Button>
            ) : null}
            {canCreateExpense ? (
              <Button
                onClick={() => {
                  setExpenseOpen(true);
                }}
              >
                New expense
              </Button>
            ) : null}
            {canExport ? (
              <>
                <MonthSelector
                  value={period}
                  onChange={setExportMonth}
                  months={exportMonths}
                />
                <Button
                  variant="secondary"
                  disabled={exportReport.isPending}
                  onClick={() => {
                    void exportReport.mutateAsync({
                      format: "csv",
                      filters: transactionExportFilters(),
                    });
                  }}
                >
                  Export CSV
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <ChipGroup>
          {TABS.map((entry) => (
            <Chip
              key={entry.id}
              active={tab === entry.id}
              onClick={() => {
                selectTab(entry.id);
              }}
            >
              {entry.label}
              {entry.id === "submitted" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </Chip>
          ))}
        </ChipGroup>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search payee, voucher, programme…"
        />
      </div>

      {transactions.isLoading ? (
        <LoadingSpinner label="Loading transactions…" className="py-16" />
      ) : transactions.isError ? (
        <EmptyState
          title="Could not load transactions"
          description="Check that the API is running and try again."
        />
      ) : transactions.data?.transactions.length ? (
        <>
          <Table caption="Financial transactions">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Transaction</TableHeaderCell>
                <TableHeaderCell>Programme</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell className="text-right">Amount · PKR</TableHeaderCell>
                <TableHeaderCell> </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.data.transactions.map((item) => (
                <TableRow key={`${item.kind}-${item.id}`}>
                  <TableCell>
                    <div>
                      <span className="block font-semibold text-edlab-ink">{item.party}</span>
                      <span className="text-xs text-edlab-muted">
                        {item.reference} · {item.kind}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{item.programme}</TableCell>
                  <TableCell className="text-edlab-muted">{item.date}</TableCell>
                  <TableCell>
                    <Badge
                      tone={
                        item.kind === "income"
                          ? "success"
                          : expenseStatusTone(item.status)
                      }
                    >
                      {item.displayStatus}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={[
                      "text-right tabular-nums",
                      item.kind === "income" ? "text-edlab-green" : "",
                    ].join(" ")}
                  >
                    {item.kind === "income" ? "+" : ""}
                    {formatMoney(item.amount)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setSelected(item);
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-edlab-line px-[18px] py-3 text-xs text-edlab-muted">
            <span>
              {transactions.data.transactions.length} record
              {transactions.data.transactions.length === 1 ? "" : "s"} ·{" "}
              {user?.permissions.unitScope === "all"
                ? "All programmes"
                : "Assigned programmes only"}
            </span>
            {canExport ? (
              <Button
                variant="ghost"
                size="small"
                className="px-0 text-edlab-green hover:bg-transparent"
                disabled={exportReport.isPending}
                onClick={() => {
                  void exportReport.mutateAsync({
                    format: "csv",
                    filters: transactionExportFilters(),
                  });
                }}
              >
                Export CSV
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <EmptyState
          title="No transactions yet"
          description="Record income or create an expense to get started. All entries are scoped to your authorized programmes."
          action={
            canCreateReceipt ? (
              <Button
                onClick={() => {
                  setIncomeOpen(true);
                }}
              >
                Record income
              </Button>
            ) : undefined
          }
        />
      )}

      {user && funds.data && incomeCategories.data && expenseCategories.data ? (
        <TransactionDetailModal
          item={selected}
          onClose={() => {
            setSelected(null);
          }}
          user={user}
          units={units}
          funds={funds.data.funds}
          incomeCategories={incomeCategories.data.categories}
          expenseCategories={expenseCategories.data.categories}
        />
      ) : null}

      {user && units.length > 0 && funds.data && incomeCategories.data && expenseCategories.data ? (
        <>
          <RecordIncomeModal
            open={incomeOpen}
            onClose={() => {
              setIncomeOpen(false);
            }}
            units={units}
            funds={funds.data.funds}
            categories={incomeCategories.data.categories}
            bankAccounts={bankAccounts.data?.bankAccounts ?? []}
          />
          <NewExpenseModal
            open={expenseOpen}
            onClose={() => {
              setExpenseOpen(false);
            }}
            units={units}
            funds={funds.data.funds}
            categories={expenseCategories.data.categories}
          />
        </>
      ) : null}
    </div>
  );
}
